import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, RadioGroup, FormControlLabel, Radio, FormLabel,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import { createBusinessProcess } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";
import {
  BUSINESS_PROCESS_CLASS_GROUPS,
  getGroupsForType,
} from "../../constants/businessProcessClass";

function getLabelForClassValue(value, t) {
  for (const group of BUSINESS_PROCESS_CLASS_GROUPS) {
    const found = group.values.find((v) => v.value === value);
    if (found) return t(found.labelKey);
  }
  return "";
}

const DEFAULT_TYPE_KEY = "CoreProcess";
const DEFAULT_GROUPS = getGroupsForType(DEFAULT_TYPE_KEY);
const DEFAULT_GROUP = DEFAULT_GROUPS[0];
const DEFAULT_CLASS = DEFAULT_GROUP.values[0].value;

export default function BusinessProcessCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [selectedTypeKey, setSelectedTypeKey] = useState(DEFAULT_TYPE_KEY);
  const [selectedGroupKey, setSelectedGroupKey] = useState(DEFAULT_GROUP.key);
  const [form, setForm] = useState({
    businessProcessName: "",
    businessProcessDescription: "",
    businessProcessType: DEFAULT_TYPE_KEY,
    businessProcessClass: DEFAULT_CLASS,
    url: "",
  });

  const handleChange = (e) => {
    if (e.target.name === "businessProcessClass") {
      const label = getLabelForClassValue(e.target.value, t);
      setForm((f) => ({
        ...f,
        businessProcessClass: e.target.value,
        businessProcessName: label,
        businessProcessDescription: label,
      }));
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const handleTypeChange = (e) => {
    const newTypeKey = e.target.value;
    setSelectedTypeKey(newTypeKey);

    if (newTypeKey === "other") {
      setForm((f) => ({ ...f, businessProcessClass: "None", businessProcessName: "", businessProcessDescription: "" }));
      return;
    }

    const groups = getGroupsForType(newTypeKey);
    const firstGroup = groups[0];
    const firstValue = firstGroup.values[0].value;
    const label = getLabelForClassValue(firstValue, t);
    setSelectedGroupKey(firstGroup.key);
    setForm((f) => ({
      ...f,
      businessProcessType: newTypeKey,
      businessProcessClass: firstValue,
      businessProcessName: label,
      businessProcessDescription: label,
    }));
  };

  const handleGroupChange = (e) => {
    const newGroupKey = e.target.value;
    const group = BUSINESS_PROCESS_CLASS_GROUPS.find((g) => g.key === newGroupKey);
    const firstValue = group.values[0].value;
    const label = getLabelForClassValue(firstValue, t);
    setSelectedGroupKey(newGroupKey);
    setForm((f) => ({
      ...f,
      businessProcessClass: firstValue,
      businessProcessName: label,
      businessProcessDescription: label,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createBusinessProcess({
        ...form,
        companyId: COMPANY_ID,
        businessDomainId,
      });
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/business-processes`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  const groupsForType = selectedTypeKey !== "other" ? getGroupsForType(selectedTypeKey) : [];
  const showGroupSelector = groupsForType.length > 1;
  const currentGroup = showGroupSelector
    ? (groupsForType.find((g) => g.key === selectedGroupKey) || groupsForType[0])
    : groupsForType[0];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("businessProcess.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              multiline
              label={t("businessProcess.name")}
              name="businessProcessName"
              value={form.businessProcessName}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("businessProcess.description")}
              name="businessProcessDescription"
              value={form.businessProcessDescription}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                {t("businessProcess.class")}
              </FormLabel>

              {/* Type + その他 */}
              <RadioGroup
                row
                value={selectedTypeKey}
                onChange={handleTypeChange}
                sx={{ mb: 1, gap: 1 }}
              >
                <FormControlLabel value="CoreProcess" control={<Radio />} label={t("businessProcess.type.coreProcess")} />
                <FormControlLabel value="BusinessSupportProcess" control={<Radio />} label={t("businessProcess.type.businessSupportProcess")} />
                <FormControlLabel value="EnterpriseSupportProcess" control={<Radio />} label={t("businessProcess.type.enterpriseSupportProcess")} />
                <FormControlLabel value="other" control={<Radio />} label={t("businessProcess.type.other")} />
              </RadioGroup>

              {/* Category (only when multiple groups) */}
              {showGroupSelector && (
                <RadioGroup
                  row
                  value={selectedGroupKey}
                  onChange={handleGroupChange}
                  sx={{ mb: 1, gap: 1 }}
                >
                  {groupsForType.map((g) => (
                    <FormControlLabel
                      key={g.key}
                      value={g.key}
                      control={<Radio size="small" />}
                      label={t(g.labelKey)}
                    />
                  ))}
                </RadioGroup>
              )}

              {/* Values */}
              {currentGroup && (
                <Box sx={{ pl: 2, borderLeft: "3px solid", borderColor: "primary.main" }}>
                  <RadioGroup
                    name="businessProcessClass"
                    value={form.businessProcessClass}
                    onChange={handleChange}
                  >
                    {currentGroup.values.map((v) => (
                      <FormControlLabel
                        key={v.value}
                        value={v.value}
                        control={<Radio size="small" />}
                        label={t(v.labelKey)}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              )}
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              label={t("common.url")}
              name="url"
              value={form.url}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">
            {t("common.add")}
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/business-processes`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
